import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  Facebook, 
  Instagram, 
  Youtube, 
  Github, 
  MessageCircle
} from 'lucide-react';

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const SHEET_URL = 'https://script.google.com/macros/s/AKfycbw4WwHOwKKxBXHoPBjJWP50dSgLPIqxtJJbxlXp1CyOvc1kUMr25DLjLWhi6z74s05sxw/exec';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const params = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });
      await new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = img.onerror = () => resolve();
        img.src = `${SHEET_URL}?${params.toString()}`;
      });
      setSnackbarSeverity('success');
      setSnackbarMessage('Thank you for your message! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch {
      setSnackbarSeverity('error');
      setSnackbarMessage('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
      setSnackbarOpen(true);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut' as any,
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        pt: { xs: 12, md: 14 },
        pb: 4,
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header Section */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  mb: 2,
                  background: 'linear-gradient(45deg, #6366f1, #ec4899)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Get in Touch
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                Have questions about our courses? Want to collaborate? 
                We'd love to hear from you!
              </Typography>
            </Box>
          </motion.div>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
            {/* Contact Information */}
            <Box sx={{ flex: { xs: '1', md: '0 0 33.333%' } }}>
              <motion.div variants={itemVariants}>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
                    Contact Information
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Card sx={{ bgcolor: 'background.paper', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Mail size={20} color="white" />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            contact@revivingindia.com
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>

                    <Card sx={{ bgcolor: 'background.paper', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: '50%',
                            bgcolor: 'secondary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Phone size={20} color="white" />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            +91 9716817710
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>

                    <Card sx={{ bgcolor: 'background.paper', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: '50%',
                            bgcolor: 'success.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <MapPin size={20} color="white" />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Address
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            Meena Bazar, Near Patna Nagar Nigam, 800007
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                </Box>

                {/* Social Media */}
                <motion.div variants={itemVariants}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                    Follow Us
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {[
                      { Icon: Facebook, href: '#', color: '#1877F2', label: 'Facebook' },
                      { Icon: Instagram, href: 'https://www.instagram.com/revivingindia/', color: '#E4405F', label: 'Instagram' },
                      { Icon: Youtube, href: 'https://www.youtube.com/@RevivingIndia', color: '#FF0000', label: 'YouTube' },
                      { Icon: Github, href: '#', color: '#333', label: 'GitHub' },
                      { Icon: MessageCircle, href: '#', color: '#25D366', label: 'WhatsApp' }
                    ].map(({ Icon, href, color, label }) => (
                      <IconButton
                        key={label}
                        component="a"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        sx={{
                          color: 'text.primary',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            color: color,
                            backgroundColor: `${color}15`,
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <Icon size={24} />
                      </IconButton>
                    ))}
                  </Box>
                </motion.div>
              </motion.div>
            </Box>

            {/* Contact Form */}
            <Box sx={{ flex: { xs: '1', md: '0 0 66.666%' } }}>
              <motion.div variants={itemVariants}>
                <Card sx={{ bgcolor: 'background.paper', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
                      Send us a Message
                    </Typography>
                    
                    <form onSubmit={handleSubmit}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                          <TextField
                            fullWidth
                            label="Your Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: 'primary.main',
                                },
                              },
                            }}
                          />
                          <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: 'primary.main',
                                },
                              },
                            }}
                          />
                        </Box>
                        <TextField
                          fullWidth
                          label="Subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                        />
                        <TextField
                          fullWidth
                          label="Message"
                          name="message"
                          multiline
                          rows={6}
                          value={formData.message}
                          onChange={handleInputChange}
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                        />
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={isSubmitting}
                          endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send size={20} />}
                          sx={{
                            background: 'linear-gradient(45deg, #6366f1, #ec4899)',
                            px: 4,
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            borderRadius: 3,
                            '&:hover': {
                              background: 'linear-gradient(45deg, #4f46e5, #db2777)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 10px 25px rgba(99, 102, 241, 0.4)',
                            },
                          }}
                        >
                          {isSubmitting ? 'Sending...' : 'Send Message'}
                        </Button>
                      </Box>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            </Box>
          </Box>

          {/* Additional Info Section */}
          <motion.div variants={itemVariants}>
            <Box sx={{ mt: 8 }}>
              <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 600, textAlign: 'center' }}>
                Find Us on Google Maps
              </Typography>
              <Box sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                border: '1px solid rgba(99, 102, 241, 0.2)',
                boxShadow: '0 10px 25px rgba(99, 102, 241, 0.15)'
              }}>
                <iframe
                  title="Reviving India Location"
                  src="https://www.google.com/maps?q=Meena%20Bazar%20Near%20Patna%20Nagar%20Nigam%20800007&output=embed"
                  width="100%"
                  height="380"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </Box>
            </Box>
          </motion.div>
        </motion.div>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactUs;
